import { useCallback, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faCloudArrowUp, faExclamationTriangle, faFile, faRotateLeft, faScissors, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { medPromptBrowserTitle } from '@medprompt/shared';
import { useDocumentUploadsContext } from '../DocumentUploadsContext';
import usePdfSplitter from './usePdfSplitter';
import usePdfSplitterSeam from './usePdfSplitterSeam';
import PdfSplitterDropZone from './PdfSplitterDropZone';
import PdfSplitterSeam from './PdfSplitterSeam';
import PdfSplitterFilmstrip from './PdfSplitterFilmstrip';
import PdfSplitterFooter from './PdfSplitterFooter';
import PdfSplitterPreview from './PdfSplitterPreview';
import './PdfSplitterView.css';

const PdfSplitterView = () => {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const sourceLabel = location.state?.label || null;
  const { handleUploadSlices } = useDocumentUploadsContext();

  const splitter = usePdfSplitter({ documentId, sourceLabel });
  const seam = usePdfSplitterSeam(splitter.numPages);
  const {
    createSliceFiles,
    toggleCut,
    pdfFile,
    pdfDoc,
    numPages,
    isLoading,
    error,
    isExistingDocMode,
    reset,
    excludedPages,
    pageGroupIndex,
    cutAfter,
    toggleExclude,
  } = splitter;

  useEffect(() => {
    document.title = medPromptBrowserTitle('Split PDF');
  }, []);

  const handleBack = useCallback(() => navigate('/'), [navigate]);

  const handleUpload = useCallback(() => {
    const payload = createSliceFiles();
    if (!payload) return;
    handleUploadSlices(payload.files, payload.options);
    navigate('/');
  }, [createSliceFiles, handleUploadSlices, navigate]);

  const handleFilmstripCut = useCallback((pageNumber) => {
    toggleCut(pageNumber);
    seam.selectPage(pageNumber);
  }, [toggleCut, seam.selectPage]);

  const loaded = pdfFile && pdfDoc && !isLoading;
  const showLoading = isLoading || (!!documentId && !pdfFile && !error);
  const leftPage = seam.seamPage;
  const rightPage = leftPage != null && leftPage < numPages ? leftPage + 1 : null;

  return (
    <div className="pdf-splitter-view">
      <div className="pdf-splitter-header">
        <button type="button" className="header-button" onClick={handleBack}>
          <FontAwesomeIcon icon={faArrowLeft} /> Back to Documents
        </button>
        <h1 className="pdf-splitter-title">
          <FontAwesomeIcon icon={faScissors} /> Split PDF
        </h1>
        {loaded && (
          <div className="pdf-splitter-fileinfo">
            <FontAwesomeIcon icon={faFile} />
            <span className="pdf-splitter-filename" title={pdfFile.name}>{pdfFile.name}</span>
            <span className="pdf-splitter-pagecount">
              {numPages} page{numPages !== 1 ? 's' : ''}
            </span>
          </div>
        )}
        {loaded && (
          <div className="pdf-splitter-header-actions">
            {!isExistingDocMode && (
              <button type="button" className="header-button" onClick={reset}>
                <FontAwesomeIcon icon={faRotateLeft} /> Start over
              </button>
            )}
            <button type="button" className="header-button" onClick={handleBack}>Cancel</button>
            <button
              type="button"
              className="header-button header-button-primary"
              onClick={handleUpload}
              disabled={!splitter.canUpload}
            >
              {splitter.isComputing ? (
                <><FontAwesomeIcon icon={faSpinner} spin /> Calculating sizes&hellip;</>
              ) : (
                <><FontAwesomeIcon icon={faCloudArrowUp} /> Upload {splitter.documentCount} document{splitter.documentCount !== 1 ? 's' : ''}</>
              )}
            </button>
          </div>
        )}
      </div>

      <div className="pdf-splitter-body">
        {error && (
          <div className="pdf-splitter-error">
            <FontAwesomeIcon icon={faExclamationTriangle} /> {error}
          </div>
        )}

        {showLoading && (
          <div className="pdf-splitter-loading">
            <FontAwesomeIcon icon={faSpinner} spin /> Loading PDF&hellip;
          </div>
        )}

        {!pdfFile && !showLoading && !documentId && (
          <PdfSplitterDropZone onFile={splitter.loadFile} />
        )}

        {loaded && leftPage != null && (
          <>
            <p className="pdf-splitter-hint">
              Click the scissors between the large pages, or between the thumbnails, to split after the left page.
              Arrow keys move the boundary. Untick a page to discard it. Each coloured group becomes one uploaded document.
            </p>
            <PdfSplitterSeam
              pdfDoc={pdfDoc}
              numPages={numPages}
              leftPage={leftPage}
              rightPage={rightPage}
              leftExcluded={excludedPages.has(leftPage)}
              rightExcluded={rightPage != null && excludedPages.has(rightPage)}
              leftSegmentIndex={pageGroupIndex[leftPage] ?? 0}
              rightSegmentIndex={rightPage != null ? (pageGroupIndex[rightPage] ?? 0) : 0}
              isCut={cutAfter.has(leftPage)}
              onToggleCut={() => toggleCut(leftPage)}
              onToggleExclude={toggleExclude}
              onPrevious={seam.showPreviousPage}
              onNext={seam.showNextPage}
              onOpenPreview={seam.openPreview}
            />
            <PdfSplitterFilmstrip
              pdfDoc={pdfDoc}
              numPages={numPages}
              pageGroupIndex={pageGroupIndex}
              cutAfter={cutAfter}
              excludedPages={excludedPages}
              selectedPage={leftPage}
              onSelectPage={seam.selectPage}
              onToggleCut={handleFilmstripCut}
              onToggleExclude={toggleExclude}
            />
          </>
        )}
      </div>

      {loaded && seam.previewPage != null && (
        <PdfSplitterPreview
          pdfDoc={pdfDoc}
          pageNumber={seam.previewPage}
          numPages={numPages}
          onClose={seam.closePreview}
          onPrevious={seam.showPreviewPrevious}
          onNext={seam.showPreviewNext}
        />
      )}

      {loaded && (
        <PdfSplitterFooter
          footerDocs={splitter.footerDocs}
          segmentResults={splitter.segmentResults}
          isComputing={splitter.isComputing}
          oversizedSegments={splitter.oversizedSegments}
          hasUnsplittableOversize={splitter.hasUnsplittableOversize}
          maxSliceBytes={splitter.maxSliceBytes}
          isExistingDocMode={splitter.isExistingDocMode}
          deleteSourceAfter={splitter.deleteSourceAfter}
          onDeleteSourceAfterChange={splitter.setDeleteSourceAfter}
          onToggleGroupInclusion={splitter.toggleGroupInclusion}
        />
      )}
    </div>
  );
};

export default PdfSplitterView;
